import { createSlice } from '@reduxjs/toolkit';
import {
    getEmployeesListAction,
    updateEmployeeAction,
    updateEmployeeStatusAction,
} from 'redux/action/employeesTableAction';

interface Employee {
    id: string;
    employeeId: string;
    fullName: string;
    email: string;
    phone: string;
    active: boolean;
    isActiveInApp: boolean;
    companyId: string;
    createdAt: string;
    updatedAt: string;
}

interface EmployeesTableState {
    data: Employee[];
    total: number;
    currentPage: number;
    limit: number;
    isLoading: boolean;
    fistTimeFetchLoading: boolean;
    hasLoadedOnce: boolean; // Track if data has been loaded at least once
    statusUpdateLoading: {
        [employeeId: string]: boolean;
    };
    nameUpdateLoading: {
        [employeeId: string]: boolean;
    };
    error: string | null;
}

const initialState: EmployeesTableState = {
    data: [],
    total: 0,
    currentPage: 1,
    limit: 20,
    isLoading: false,
    fistTimeFetchLoading: true,
    hasLoadedOnce: false,
    statusUpdateLoading: {},
    nameUpdateLoading: {},
    error: null,
};

const employeesTableSlice = createSlice({
    name: 'employeesTable',
    initialState,
    reducers: {
        clearEmployeesData: (state) => {
            state.data = [];
            state.total = 0;
            state.currentPage = 1;
            state.hasLoadedOnce = false; // Reset when clearing data
        },
        removeEmployeeFromList: (state, action) => {
            const employeeId = action.payload;
            state.data = state.data.filter((employee) => employee.id !== employeeId);
            // Decrement total when removing from filtered list
            if (state.total > 0) {
                state.total = state.total - 1;
            }
        },
    },
    extraReducers: (builder) => {
        // Get Employees List
        builder.addCase(getEmployeesListAction.pending, (state, action) => {
            const page = action.meta.arg?.page || 1;
            const isPagination = page > 1;
            // Only show full page loader on very first load (when we haven't loaded data before)
            if (!state.hasLoadedOnce && page === 1 && !isPagination) {
                state.fistTimeFetchLoading = true;
            } else {
                // For all subsequent loads (search/sort/filter), show table loader only
                state.fistTimeFetchLoading = false;
            }
            state.isLoading = true;
            state.error = null;
        });
        builder.addCase(getEmployeesListAction.fulfilled, (state, action) => {
            const responseData = action.payload.responseData;
            const employeesData = responseData?.data || [];
            const total = responseData?.total || 0;
            const currentPage = responseData?.currentPage || 1;
            const limit = responseData?.limit || 20;

            if (action.payload.isPagination) {
                // Append for pagination
                state.data = [...state.data, ...employeesData];
            } else {
                // Replace for initial load/search/filter/sort
                state.data = employeesData;
            }
            state.total = total;
            state.currentPage = currentPage;
            state.limit = limit;
            state.isLoading = false;
            state.fistTimeFetchLoading = false;
            state.hasLoadedOnce = true; // Mark that we've loaded data at least once
        });
        builder.addCase(getEmployeesListAction.rejected, (state, action: any) => {
            state.error = action.payload?.message || 'Failed to fetch employees';
            state.isLoading = false;
            state.fistTimeFetchLoading = false;
            // Don't set hasLoadedOnce on error, so we can retry with full loader if needed
        });

        // Update Employee Status
        builder.addCase(updateEmployeeStatusAction.pending, (state, action) => {
            const employeeId = action.meta.arg.employeeId;
            state.statusUpdateLoading[employeeId] = true;
        });
        builder.addCase(updateEmployeeStatusAction.fulfilled, (state, action) => {
            const updatedEmployee = action.payload.updatedEmployee;
            // Update specific employee in data array
            state.data = state.data.map((employee) =>
                employee.id === updatedEmployee.id ? updatedEmployee : employee
            );
            state.statusUpdateLoading[updatedEmployee.id] = false;
        });
        builder.addCase(updateEmployeeStatusAction.rejected, (state, action: any) => {
            const employeeId = action.meta.arg.employeeId;
            state.statusUpdateLoading[employeeId] = false;
            state.error = action.payload?.message || 'Failed to update employee status';
        });

        // Update Employee (name and/or active status) — ENG-363
        builder.addCase(updateEmployeeAction.pending, (state, action) => {
            const employeeId = action.meta.arg.employeeId;
            state.nameUpdateLoading[employeeId] = true;
        });
        builder.addCase(updateEmployeeAction.fulfilled, (state, action) => {
            const updatedEmployee = action.payload.employee;
            // Replace the updated employee in place by id (same pattern as status update)
            state.data = state.data.map((employee) =>
                employee.id === updatedEmployee.id ? updatedEmployee : employee
            );
            state.nameUpdateLoading[updatedEmployee.id] = false;
        });
        builder.addCase(updateEmployeeAction.rejected, (state, action: any) => {
            const employeeId = action.meta.arg.employeeId;
            state.nameUpdateLoading[employeeId] = false;
            state.error = action.payload?.message || 'Failed to update employee';
        });
    },
});

export const { clearEmployeesData, removeEmployeeFromList } = employeesTableSlice.actions;
export default employeesTableSlice;

